<template>
  <div class="login-simple">
    <h3>Login to Continue</h3>
    
    <el-form 
      ref="loginForm"
      :model="loginForm"
      :rules="rules"
    >
      <el-form-item prop="loginType">
        <el-select v-model="loginForm.loginType" placeholder="Select login type">
          <el-option label="Student" value="Student"></el-option>
          <el-option label="Advisor" value="Advisor"></el-option>
          <el-option label="School Admin" value="SchoolAdmin"></el-option>
        </el-select>
      </el-form-item>

      <el-form-item prop="email">
        <el-input
          v-model="loginForm.email"
          :placeholder="loginForm.loginType === 'Student' ? 'Enter your email' : 'Enter admin email'"
          type="email"
        ></el-input>
      </el-form-item>

      <template v-if="loginForm.loginType === 'Student' || loginForm.loginType === 'Advisor'">
        <el-form-item prop="otp">
          <div class="otp-input-group">
            <el-input
              v-model="loginForm.otp"
              placeholder="Enter OTP code"
            ></el-input>
            <el-button
              :loading="sendingOtp"
              @click="handleSendOtp"
            >
              Send OTP Code
            </el-button>
          </div>
        </el-form-item>
      </template>

      <template v-else>
        <el-form-item prop="password">
          <el-input
            v-model="loginForm.password"
            placeholder="Enter password"
            type="password"
          ></el-input>
        </el-form-item>
      </template>

      <div class="form-actions">
        <el-button 
          type="primary" 
          :loading="loading"
          @click="handleLogin"
        >
          Login
        </el-button>
      </div>
    </el-form>
  </div>
</template>

<script>
import { createLoginOPT, loginByStudent, loginByAccountAdmin, loginByAdvisor } from '@/api/eclub'
import { setUserId, setUserName, setUserType, setUserAccountCountry, setAccountId, setUserEmail } from '@/utils/store'

export default {
  name: 'LoginSimple',
  data() {
    return {
      loginForm: {
        loginType: 'Student',
        email: '',
        otp: '',
        password: ''
      },
      loading: false,
      sendingOtp: false,
      rules: {
        loginType: [
          { required: true, message: 'Please select login type', trigger: 'change' }
        ],
        email: [
          { required: true, message: 'Please enter your email', trigger: 'blur' },
          { type: 'email', message: 'Please enter a valid email', trigger: 'blur' }
        ],
        otp: [
          { required: true, message: 'Please enter OTP code', trigger: 'blur' }
        ],
        password: [
          { required: true, message: 'Please enter password', trigger: 'blur' }
        ]
      }
    }
  },
  methods: {
    async handleSendOtp() {
      try {
        await this.$refs.loginForm.validateField('email')
        
        this.sendingOtp = true
        const response = await createLoginOPT(this.loginForm.email)

        if (response.data.code === 0) {
          this.$message.success('Sent Successfully')
        } else {
          this.$message.error('Verification code sent failed. Please try again.')
        }
      } catch (error) {
        if (error === false) return // Form validation failed
        console.error('Send OTP error:', error)
        this.$message.error('Failed to send login code. Please try again.')
      } finally {
        this.sendingOtp = false
      }
    },
    async handleLogin() {
      try {
        await this.$refs.loginForm.validate()
        this.loading = true

        let response
        if (this.loginForm.loginType === 'Student') {
          response = await loginByStudent(this.loginForm.email, this.loginForm.otp)
        } else if (this.loginForm.loginType === 'Advisor') {
          response = await loginByAdvisor({
            contact_email: this.loginForm.email,
            verify_code: this.loginForm.otp
          })
        } else if (this.loginForm.loginType === 'SchoolAdmin') {
          response = await loginByAccountAdmin(this.loginForm.email, this.loginForm.password)
        }

        const result = response.data
        if (result.code === 0) {
          setUserId(result.data._id.$id)
          
          let account_country = "China"
          if (result.data.account_country) {
            account_country = result.data.account_country
          }
          setUserAccountCountry(account_country)
          
          let user_name
          if (this.loginForm.loginType === 'Student') {
            user_name = result.data.student_givenName_pinyin + ' ' + result.data.student_lastName_pinyin
          } else if (this.loginForm.loginType === 'SchoolAdmin') {
            user_name = result.data.account_nameEN || result.data.account_name
          } else {
            user_name = result.data.contact_givenName + ' ' + result.data.contact_lastName
          }
          
          setUserName(user_name)
          setUserType(this.loginForm.loginType.toLowerCase())
          setAccountId(this.loginForm.loginType === 'SchoolAdmin' ? result.data._id.$id : result.data.account_id)
          setUserEmail(this.loginForm.email)
          
          this.$UserData.user_name = user_name
          this.$UserData.account_country = account_country
          this.$UserData.email = this.loginForm.email
          
          this.$message.success('Login successful')
          this.$emit('login-success', {
            user_id: result.data._id.$id,
            user_name,
            user_type: this.loginForm.loginType.toLowerCase(),
            account_country
          })
          
          this.$emit('close')
          
          // const routes = {
          //   Student: '/home/studentclubs',
          //   SchoolAdmin: '/home/schoolclubs',
          //   Advisor: '/home/adviserclubs'
          // }
          // this.$router.push(routes[this.loginForm.loginType])
        } else {
          this.$message.error(result.msg || 'Login failed')
        }
      } catch (error) {
        if (error === false) return // Form validation failed
        console.error('Login error:', error)
        this.$message.error('Login failed. Please try again.')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.login-simple {
  padding: 20px;
  max-width: 400px;
  margin: 0 auto;

  h3 {
    text-align: center;
    margin: 0 0 24px;
    color: #303133;
  }

  .otp-input-group {
    display: flex;
    gap: 12px;

    .el-input {
      flex: 1;
    }
  }

  .form-actions {
    margin: 24px 0;
    text-align: center;

    .el-button {
      width: 100%;
    }
  }
}

.el-select {
  width: 100%;
}
</style> 